import { type TeamStats } from "@/api/tournamentAnalysis";
import { type QuickStats } from "@/api/quickStats";
import { useState } from "react";
import { X, TrendingUp, Info } from "lucide-react";
import { Bar, BarChart, CartesianGrid, XAxis, YAxis, LabelList, ReferenceLine } from "recharts";
import { ChartConfig, ChartContainer, ChartTooltip } from "@/components/shadcn/chart";
import { Loader } from "../../common/loader";

interface TournamentAnalysisQuickStatsCardProps {
    teamStats?: TeamStats;
    isLoading?: boolean;
    tournamentId?: number;
    quickStatsData?: QuickStats;
    quickStatsLoading?: boolean;
}

const chartConfig = {
    percentage: {
        label: "Percentage",
        color: "hsl(var(--chart-1))",
    },
} satisfies ChartConfig;

type StatKey = keyof QuickStats;

const statKeyMapping: Record<string, StatKey> = {
    "Shot Accuracy": "shotsAccuracy",
    "Face-off Win %": "faceoffWin",
    "Team On Ice Save %": "saves",
    "Puck Contest Win %": "puckContestWin",
    "Corsi %": "corsi",
    "Fenwick %": "fenwick",
};

export function TournamentAnalysisQuickStatsCard({
    teamStats,
    isLoading,
    tournamentId,
    quickStatsData,
    quickStatsLoading,
}: TournamentAnalysisQuickStatsCardProps) {
    const [selectedStat, setSelectedStat] = useState<string | null>(null);

    const quickStats = [
        { label: "Shot Accuracy", value: teamStats?.shotAccuracy, color: "green" },
        { label: "Face-off Win %", value: teamStats?.faceOffWinPercentage, color: "yellow" },
        { label: "Team On Ice Save %", value: teamStats?.savePercentage, color: "purple" },
        { label: "Puck Contest Win %", value: teamStats?.puckContestWinPercentage, color: "blue" },
        { label: "Corsi %", value: teamStats?.corsiPercentage, color: "red" },
        { label: "Fenwick %", value: teamStats?.fenwickPercentage, color: "cyan" },
    ];

    const handleStatClick = (statLabel: string) => {
        if (tournamentId) {
            setSelectedStat(statLabel);
        }
    };

    const handleCloseModal = () => {
        setSelectedStat(null);
    };

    const getChartData = () => {
        if (!quickStatsData || !selectedStat) return [];

        const statKey = statKeyMapping[selectedStat];
        if (!statKey || !quickStatsData[statKey]) return [];

        return quickStatsData[statKey].map((player) => ({
            name: `${player.firstName} ${player.lastName}`,
            percentage: player.percentage,
            playerId: player.playerId,
        }));
    };

    const getStatColor = (statLabel: string) => {
        const stat = quickStats.find((s) => s.label === statLabel);
        return stat?.color || "blue";
    };

    const getTeamAverageValue = (statLabel: string) => {
        const stat = quickStats.find((s) => s.label === statLabel);
        return stat?.value || 0;
    };

    if (isLoading) {
        return (
            <div className="bg-surface-container-lowest rounded-xl p-6 flex items-center justify-center shadow-sm">
                <Loader message="Loading quick stats..." />
            </div>
        );
    }

    if (!teamStats && !isLoading) {
        return (
            <div className="bg-surface-container-lowest rounded-xl p-6 flex items-center justify-center shadow-sm flex-1 min-h-0">
                <p className="text-on-surface-variant">No quick stats available</p>
            </div>
        );
    }

    return (
        <>
            <div
                id="onboarding-tournament-quick-stats"
                className="bg-surface-container-lowest rounded-xl overflow-hidden shadow-sm flex flex-col flex-1 min-h-0"
            >
                <div className="space-y-6 flex-1 overflow-y-auto p-6">
                    {quickStats.map((stat) => {
                        const displayValue = stat.value ? `${stat.value.toFixed(1)}%` : "N/A";
                        const percentage = stat.value || 0;
                        const hasData = tournamentId && stat.value;

                        return (
                            <div key={stat.label}>
                                <div className="flex justify-between items-center mb-2">
                                    <button
                                        onClick={() => (hasData ? handleStatClick(stat.label) : undefined)}
                                        className={`flex items-center gap-2 text-sm text-on-surface text-left ${
                                            hasData
                                                ? "hover:text-on-surface cursor-pointer transition-colors"
                                                : "cursor-default"
                                        }`}
                                        disabled={!hasData}
                                    >
                                        <span>{stat.label}</span>
                                        {hasData && (
                                            <Info className="w-3 h-3 text-on-surface-variant hover:text-on-surface" />
                                        )}
                                    </button>
                                    <span className={`text-sm font-semibold text-general-${stat.color}`}>
                                        {displayValue}
                                    </span>
                                </div>
                                <div className="w-full bg-surface-container rounded-full h-2">
                                    <div
                                        className={`h-2 rounded-full transition-all duration-500 bg-gradient-general-${stat.color}`}
                                        style={{ width: `${Math.min(percentage, 100)}%` }}
                                    />
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
            {selectedStat && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
                    <div className="bg-surface-container-low border border-outline-variant/20 rounded-md shadow-2xl w-full max-w-4xl max-h-[90vh] mx-4 overflow-hidden flex flex-col">
                        <div className="flex items-start justify-between p-4 md:p-6 border-b border-outline-variant/20 flex-shrink-0 bg-surface-container-high">
                            <div className="flex items-start gap-3">
                                <TrendingUp className={`w-5 h-5 mt-0.5 text-general-${getStatColor(selectedStat)}`} />
                                <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-4">
                                    <h2 className="text-base md:text-xl font-headline font-bold text-on-surface">
                                        {selectedStat} Breakdown
                                    </h2>
                                    <div className="flex items-center gap-2 px-3 py-1 bg-surface-container-low rounded-md w-fit">
                                        <span className="text-sm text-on-surface-variant">Team Average:</span>
                                        <span
                                            className={`text-lg font-bold text-general-${getStatColor(selectedStat)}`}
                                        >
                                            {(() => {
                                                const stat = quickStats.find((s) => s.label === selectedStat);
                                                return stat?.value ? `${stat.value.toFixed(1)}%` : "N/A";
                                            })()}
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <button
                                onClick={handleCloseModal}
                                className="p-2 text-on-surface-variant hover:text-on-surface rounded-md hover:bg-surface-container transition-colors cursor-pointer"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <div className="flex-1 min-h-0 overflow-y-auto p-4 md:p-6">
                            {quickStatsLoading ? (
                                <div className="flex items-center justify-center h-96">
                                    <Loader message="Loading player stats..." />
                                </div>
                            ) : (
                                (() => {
                                    const chartData = getChartData();
                                    const statColor = getStatColor(selectedStat);
                                    const teamAverageValue = getTeamAverageValue(selectedStat);

                                    if (chartData.length === 0) {
                                        return (
                                            <div className="flex items-center justify-center h-96">
                                                <p className="text-on-surface-variant">No player data available</p>
                                            </div>
                                        );
                                    }

                                    return (
                                        <div className="h-72 md:h-96 overflow-x-auto overflow-y-hidden">
                                            <ChartContainer
                                                config={chartConfig}
                                                className="h-full"
                                                style={{
                                                    minWidth: `${Math.max(320, chartData.length * 52)}px`,
                                                }}
                                            >
                                                <BarChart
                                                    data={chartData}
                                                    margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                                                >
                                                    <CartesianGrid
                                                        vertical={false}
                                                        stroke="rgba(148, 163, 184, 0.1)"
                                                        strokeDasharray="3 3"
                                                    />
                                                    <XAxis
                                                        dataKey="name"
                                                        tickLine={false}
                                                        axisLine={false}
                                                        tickMargin={8}
                                                        tick={{
                                                            fill: "var(--color-on-surface-variant)",
                                                            fontSize: 12,
                                                        }}
                                                        angle={-45}
                                                        textAnchor="end"
                                                        height={60}
                                                    />
                                                    <YAxis
                                                        domain={[0, 100]}
                                                        tickLine={false}
                                                        axisLine={false}
                                                        tick={{
                                                            fill: "var(--color-on-surface-variant)",
                                                            fontSize: 12,
                                                        }}
                                                        tickFormatter={(value) => `${value}%`}
                                                    />
                                                    <ChartTooltip
                                                        cursor={{
                                                            fill: `var(--color-general-${statColor})`,
                                                            fillOpacity: 0.1,
                                                        }}
                                                        content={({ active, payload, label }) => {
                                                            if (!active || !payload?.length) return null;

                                                            const value = payload[0]?.value;
                                                            const playerData = payload[0]?.payload;
                                                            const playerId = playerData?.playerId;

                                                            return (
                                                                <div className="chart-tooltip rounded-md">
                                                                    <div className="flex flex-col gap-2">
                                                                        <div className="flex items-baseline gap-2 text-sm text-general-blue">
                                                                            <span className="font-semibold">
                                                                                {label}
                                                                            </span>
                                                                            {playerId && (
                                                                                <span className="text-xs text-on-surface-variant">
                                                                                    {playerId}
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                        <div className="flex items-center gap-2">
                                                                            <div
                                                                                className="w-3 h-3 rounded-full"
                                                                                style={{
                                                                                    backgroundColor: `var(--color-general-${statColor})`,
                                                                                }}
                                                                            />
                                                                            <span className="text-sm font-medium text-on-surface">
                                                                                {selectedStat}:
                                                                            </span>
                                                                            <span
                                                                                className="text-lg font-bold"
                                                                                style={{
                                                                                    color: `var(--color-general-${statColor})`,
                                                                                }}
                                                                            >
                                                                                {typeof value === "number"
                                                                                    ? `${value.toFixed(0)}%`
                                                                                    : value}
                                                                            </span>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            );
                                                        }}
                                                    />
                                                    <Bar
                                                        dataKey="percentage"
                                                        fill={`var(--color-general-${statColor})`}
                                                        radius={[4, 4, 0, 0]}
                                                    >
                                                        <LabelList
                                                            dataKey="percentage"
                                                            position="top"
                                                            style={{
                                                                fill: "var(--color-on-surface)",
                                                                fontSize: "11px",
                                                                fontWeight: "bold",
                                                            }}
                                                            formatter={(label: React.ReactNode) => {
                                                                if (typeof label === "number") {
                                                                    return `${Math.round(label)}`;
                                                                }
                                                                return String(label ?? "");
                                                            }}
                                                        />
                                                    </Bar>
                                                    <ReferenceLine
                                                        y={teamAverageValue}
                                                        stroke="#ef4444"
                                                        strokeDasharray="8 4"
                                                        strokeWidth={2}
                                                    />
                                                </BarChart>
                                            </ChartContainer>
                                        </div>
                                    );
                                })()
                            )}
                        </div>
                        <div className="px-6 py-4 border-t border-outline-variant/20 bg-surface-container-highest flex-shrink-0">
                            <p className="text-sm text-on-surface-variant">
                                Individual player contributions to team {selectedStat.toLowerCase()}
                            </p>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
